// Section file — funnel team (es-419, informal "tú").
// Sections: login, register, registerProfile, registerSocial, registerCheckout,
// upsell, purchase, purchaseCheckout, purchaseUsername.
// Must keep an IDENTICAL key tree with en.funnel.js.
import React from "react";

const lang = {
  login: {
    passwordsDoNotMatch: "Las contraseñas no coinciden",
    resetSuccess: "Contraseña restablecida con éxito. Inicia sesión con tu nueva contraseña.",
    genericError: "Ocurrió un error. Por favor, inténtalo de nuevo.",
    newPasswordLabel: "Nueva contraseña",
    newPasswordPlaceholder: "Ingresa tu nueva contraseña",
    passwordHint: "La contraseña debe tener al menos 8 caracteres",
    confirmPasswordLabel: "Confirmar contraseña",
    confirmPasswordPlaceholder: "Confirma tu nueva contraseña",
    resetPasswordButton: "Restablecer contraseña",
    resetSent: "Instrucciones de restablecimiento enviadas a tu correo",
    resetFailed: "No se pudieron enviar las instrucciones de restablecimiento",
    loading: "Cargando...",
    resetModalTitle: "Restablece tu contraseña",
    resetModalBody: "Ingresa tu dirección de correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.",
    emailAddressLabel: "Correo electrónico",
    emailPlaceholder: "name@example.com",
    sendResetInstructions: "Enviar instrucciones",
    heading: "Iniciar sesión",
    subheading: "Inicia sesión con tu correo y contraseña para acceder al panel.",
    enterEmailPlaceholder: "Ingresa tu correo",
    passwordPlaceholder: "Contraseña",
    loginButton: "Iniciar sesión",
    forgotPassword: " ¿Olvidaste tu contraseña? ",
    noAccount: (
      <>
        ¿No tienes una cuenta? <a className="text-primary" href="/pricing">Compra un plan</a>
      </>
    ),
  },
  register: {
    stepSelectPlan: "Elegir plan",
    stepSetupAccount: "Configurar cuenta",
    stepBoostGrowth: "Impulsar crecimiento",
    stepGetStarted: "Comenzar",
    continueToCheckout: "Continuar al pago",
    profileLoadError: "No se pudo cargar el perfil. Por favor, inténtalo más tarde.",
    chartIconAlt: "Ícono de gráfica",
  },
  registerProfile: {
    heading: "Configura tu cuenta",
    subheading: "Regístrate con tu correo para acceder al panel.",
    emailPlaceholder: "Correo electrónico",
    emailAriaLabel: "Correo electrónico",
    didYouMean: (suggestion) => (
      <>
        ¿Quisiste decir <strong>{suggestion}</strong>?
      </>
    ),
    continue: "Continuar",
    alreadyHaveAccount: (
      <>
        ¿Ya tienes una cuenta?{" "}
        <a className="text-primary" href="/login">
          Inicia sesión aquí
        </a>
      </>
    ),
  },
  registerSocial: {
    heading: "Vincula tu perfil",
    subheading: "Necesitamos tu perfil para empezar a aumentar tus seguidores. Tus credenciales nunca se almacenan.",
    searchFailed: "No se pudo buscar la cuenta.",
    linkedSuccess: (username, platform) => `Cuenta ${username} de ${platform} vinculada con éxito.`,
    profileNotFound: "Perfil no encontrado. Verifica tu nombre de usuario e inténtalo de nuevo.",
    usernamePlaceholder: "Ingresa el nombre de usuario",
    usernameAriaLabel: "Nombre de usuario",
    searchButton: "Buscar",
    linkButton: "Vincular",
    continue: "Continuar",
    skipStep: (
      <>
        ¿Quieres saltarte este paso?{" "}
        <a className="text-primary" href="/dashboard">
          Continuar sin vincular
        </a>
      </>
    ),
  },
  registerCheckout: {
    unknownError: "Error desconocido",
    invalidMonth: "Mes inválido",
    cardExpired: "La tarjeta está vencida",
    errorUpdatingEmail: "Error al actualizar el correo",
    enterPassword: "Por favor, ingresa una contraseña",
    setPasswordFailed: "No se pudo establecer la contraseña. Por favor, inténtalo de nuevo.",
    allSet: "¡Todo listo!",
    planActive: (plan) => `Tu plan ${plan} está activo. Crea una contraseña para acceder a tu panel.`,
    createPasswordPlaceholder: "Crea una contraseña",
    setPasswordButton: "Establecer contraseña e ir al panel",
    perMonthStrike: (n) => `$${n}/mes`,
    perMonthSuffix: "/mes",
    perYear: (n) => `$${n}/año`,
    savePerYear: (n) => ` · Ahorra $${n} al año`,
    followersLine: (range) => `${range} seguidores mensuales. El crecimiento comienza en menos de 24 horas.`,
    chargedToday: (amount) => (
      <>
        Hoy se te cobrará <strong style={{ color: '#0a2540' }}>${amount}</strong> por 1 año de acceso.
      </>
    ),
    // Interruptor de renovación. Activado por defecto; desactivado = compra única.
    recurringLabel: "Renovar mi plan automáticamente",
    recurringOnHelp: (interval) => `Se renueva automáticamente cada ${interval}. Cancela cuando quieras.`,
    recurringOffHelp: (interval) => `Compra única — obtienes ${interval} de acceso y no se te cobrará de nuevo.`,
    recurringIntervalMonth: "mes",
    recurringIntervalYear: "año",
    recurringIntervalOneMonth: "1 mes",
    recurringIntervalOneYear: "1 año",
    annualPlanSelected: "Plan anual seleccionado",
    savingPerYear: (savings) => (
      <>
        Estás ahorrando <strong>${savings}</strong> al año
      </>
    ),
    accountLabel: "Cuenta",
    change: "Cambiar",
    edit: "Editar",
    orderSummary: "Resumen del pedido",
    planLine: (plan, interval) => `Plan ${plan} (${interval})`,
    intervalAnnual: "anual",
    intervalMonthly: "mensual",
    likesAddon: "Complemento Más Likes",
    viewsAddon: "Complemento Más Vistas",
    engagementAddon: "Complemento de Interacción",
    annualDiscount: "Descuento anual",
    totalBilledToday: "Total facturado hoy",
    dueToday: "A pagar hoy",
    thatsPerMonth: (n) => `Eso es $${n}/mes`,
    payment: "Pago",
    newCard: "Nueva tarjeta",
    cardNumberLabel: "Número de tarjeta",
    expirationLabel: "Vencimiento",
    cvcLabel: "CVC",
    expPlaceholder: "MM / AA",
    cvcPlaceholder: "CVC",
    paymentFailed: "El pago falló",
    processing: "Procesando...",
    startMyGrowth: "Iniciar mi crecimiento",
    loadingPaymentOptions: "Cargando opciones de pago...",
    secureCheckout: "Pago seguro",
    cancelAnytime: "Cancela cuando quieras",
    guarantee: "Garantía de 7 días",
    changeEmailTitle: "Cambiar correo",
    newEmailLabel: "Nueva dirección de correo",
    emailPlaceholder: "you@example.com",
    cancel: "Cancelar",
    save: "Guardar",
  },
  upsell: {
    options: {
      likes: {
        title: "Más Likes",
        description: "¡Compartiremos tus publicaciones más recientes hasta 10 veces más con tu audiencia específica después de cada publicación para maximizar los likes!",
      },
      views: {
        title: "Más Vistas",
        description: "¡Compartiremos tus publicaciones más recientes hasta 10 veces más con tu audiencia específica todos los días para maximizar las vistas y el crecimiento!",
      },
      engagement: {
        title: "Interacción",
        description: "¡Compartiremos tus publicaciones e historias hasta 12 veces más para maximizar tu interacción!",
      },
    },
    errorUpdating: (msg) => `Error al actualizar los complementos: ${msg}`,
    errorUpdatingFallback: "Error al actualizar los complementos",
    manageAddons: "Administra tus complementos",
    boostYourGrowth: "Impulsa tu crecimiento",
    manageSubheading: "Agrega o quita extras para personalizar tu experiencia de crecimiento.",
    boostSubheading: "Agrega extras poderosos para potenciar tus resultados.",
    activeList: (list) => `Activos: ${list}`,
    activeBadge: "Activo",
    perMo: "/mes",
    billChange: (isAnnualPlan, isIncrease, amount) => `Tu factura ${isAnnualPlan ? 'anual' : 'mensual'} ${isIncrease ? 'aumentará' : 'disminuirá'} $${amount}/${isAnnualPlan ? 'año' : 'mes'}`,
    saving: "Guardando...",
    saveChanges: "Guardar cambios",
    confirmTitle: "Confirmar cambios de complementos",
    changesSummary: "Resumen de cambios",
    adding: "Agregando",
    removing: "Quitando",
    yr: "año",
    mo: "mes",
    periodChange: (isAnnualPlan) => `Cambio ${isAnnualPlan ? 'anual' : 'mensual'}`,
    cancel: "Cancelar",
    processing: "Procesando...",
    confirmChanges: "Confirmar cambios",
  },
  purchase: {
    unknownError: "Error desconocido",
    purchaseComplete: "¡Compra completada!",
    errorLoggingIn: (msg) => `Error al iniciar sesión: ${msg}`,
  },
  purchaseCheckout: {
    plans: {
      pro: {
        name: "Plan Pro",
        features: [
          { feature: "Entrega instantánea", description: "Recibe tus seguidores al instante después del pago." },
          { feature: "7,500 - 10,000 seguidores", description: "Recibe de 7,500 a 10,000 seguidores por mes." },
          { feature: "500 de saldo de impulso", description: "Usa el saldo de impulso para enviar Likes, Vistas y Comentarios a tus publicaciones." },
          { feature: "Bonos diarios", description: "Gana bonos diarios para obtener más saldo" },
          { feature: "Generador de descripciones con IA", description: "Genera descripciones para tus publicaciones usando IA." },
          { feature: "Generador de ideas", description: "Obtén ideas para tu próxima publicación." },
          { feature: "Optimizador de perfil", description: "Analiza tu perfil para conseguir más seguidores." },
          { feature: "Visor anónimo de historias", description: "Mira historias de forma anónima." },
          { feature: "Descargador de publicaciones en HD", description: "Descarga publicaciones en alta calidad." },
        ],
      },
      basic: {
        name: "Plan Básico",
        features: [
          { feature: "Entrega instantánea", description: "Recibe tus seguidores al instante después del pago." },
          { feature: "2,000 - 3,000 seguidores", description: "Recibe de 2,000 a 3,000 seguidores por mes." },
          { feature: "500 de saldo de impulso", description: "Usa el saldo de impulso para enviar Likes, Vistas y Comentarios a tus publicaciones." },
          { feature: "Bonos diarios", description: "Gana bonos diarios para obtener más saldo" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
        ],
      },
      starter: {
        name: "Plan Starter",
        features: [
          { feature: "500 - 1,000 seguidores", description: "Gana de 500 a 1,000 seguidores reales por mes sin mover un dedo." },
          { feature: "$5 en créditos de impulso ⓘ", description: "Canjea tu crédito de impulso por Seguidores, Likes, Vistas y Comentarios." },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
        ],
      },
    },
    heading: "Detalles de pago",
    subheading: "Esto es para verificar tu cuenta, no se te cobrará durante tu prueba gratis de 3 días.",
    annualButton: "anual",
    save50: "Ahorra 50%",
    monthlyButton: "Mensual",
    proPlanLabel: "Plan Pro",
    fiftyOffBadge: "50% de descuento",
    monthlyPlanBadge: "Plan mensual",
    billedAnnually: (price) => `Facturado anualmente a $${price}/mes`,
    billedMonthly: (price) => `Facturado mensualmente a $${price}/mes`,
    balanceOption: (balance) => `${balance} de saldo`,
    balancePrice: (price) => `+$${price}/mes`,
    cardNumberPlaceholder: "Número de tarjeta",
    cardNumberAria: "Número de tarjeta",
    expPlaceholder: "MM / AA",
    expAria: "Fecha de vencimiento de la tarjeta",
    cvvPlaceholder: "CVV",
    cvvAria: "CVV",
    loading: "Cargando...",
    startFreeTrial: "Comenzar prueba gratis",
    whatsIncluded: "Qué incluye la prueba gratis",
  },
  purchaseUsername: {
    heading: "Configura tu cuenta",
    subheading: "Regístrate con tu correo para acceder al panel.",
    fullNamePlaceholder: "Nombre completo",
    fullNameAria: "Nombre completo",
    emailPlaceholder: "Correo electrónico",
    emailAria: "Correo electrónico",
    passwordPlaceholder: "Contraseña",
    passwordAria: "Contraseña",
    continue: "Continuar",
    alreadyHaveAccount: (
      <>
        ¿Ya tienes una cuenta?{" "}
        <a className="text-primary" href="/login">
          Inicia sesión aquí
        </a>
      </>
    ),
  },
};

export default lang;
